// iGaming Radar filters and scoring, ported from the radar's own page
// (public/filters.js, public/affiliate-filters.js, composite-interest.mjs) so
// the admin answers the same questions the same way.
import type { AffiliateClaim, AffiliateProgram, RadarBrand } from '@/api/radar';

// ── Brands ────────────────────────────────────────────────────────────────

export const FACT_LABELS: Record<string, string> = {
  website: 'Official website (per source)',
  operator: 'Operator',
  products: 'Products',
  operating_status: 'Operating status',
  founded_year: 'Founded',
  licenses: 'Licenses (as the catalog claims)',
  allowed_countries: 'Allowed countries',
  restricted_countries: 'Restricted countries',
  listed_market: 'Catalog market',
  currencies: 'Currencies',
  deposit_methods: 'Deposit methods',
  withdrawal_methods: 'Withdrawal methods',
  rating: 'Source rating',
  discovered_on_new_list: 'First seen on the New list',
  languages: 'Languages',
  providers: 'Game providers',
  affiliate_program: 'Affiliate program',
};

export function factValues(b: RadarBrand, field: string): unknown[] {
  return b.facts.filter((f) => f.field === field).flatMap((f) => (Array.isArray(f.value) ? f.value : [f.value]));
}

export function formatFact(v: unknown): string {
  if (Array.isArray(v)) return v.join(', ');
  if (v && typeof v === 'object') {
    const r = v as { value?: unknown; scale?: unknown };
    return `${r.value} / ${r.scale}`;
  }
  return String(v ?? '');
}

export interface BrandFilters {
  q: string;
  coverage: '' | 'yes' | 'no';
  source: string; operator: string; products: string; licenses: string;
  currencies: string; deposit_methods: string; country: string;
  status: '' | 'closed' | 'active' | 'unknown';
  year: string;
  website: '' | 'yes' | 'no';
  newOnly: boolean;
  ratingSource: 'lcb' | 'askgamblers';
  rating: string;
}

export const EMPTY_BRAND_FILTERS: BrandFilters = {
  q: '', coverage: '', source: '', operator: '', products: '', licenses: '', currencies: '',
  deposit_methods: '', country: '', status: '', year: '', website: '', newOnly: false, ratingSource: 'lcb', rating: '',
};

/** Lower-cased text the search box looks in: name, category, source, facts. */
export function brandHaystack(b: RadarBrand): string {
  return (b.name + ' ' + b.category + ' ' + b.source_label + ' ' + JSON.stringify(b.facts)).toLowerCase();
}

export function matchesBrand(b: RadarBrand, f: BrandFilters, haystack = brandHaystack(b)): boolean {
  if (f.q && !haystack.includes(f.q.toLowerCase())) return false;
  if ((f.coverage === 'yes' && !b.facts.length) || (f.coverage === 'no' && b.facts.length)) return false;
  if (f.source && !b.sources.some((s) => s.source_id === f.source) && b.source_label !== f.source) return false;
  for (const key of ['operator', 'products', 'licenses', 'currencies', 'deposit_methods'] as const) {
    if (f[key] && !factValues(b, key).includes(f[key])) return false;
  }
  if (f.country && ![...factValues(b, 'allowed_countries'), ...factValues(b, 'listed_market')].includes(f.country)) return false;
  if (f.status === 'unknown' && factValues(b, 'operating_status').length) return false;
  if (f.status && f.status !== 'unknown' && !factValues(b, 'operating_status').includes(f.status)) return false;
  if (f.year && !factValues(b, 'founded_year').some((y) => Number(y) >= Number(f.year))) return false;
  if ((f.website === 'yes' && !factValues(b, 'website').length) || (f.website === 'no' && factValues(b, 'website').length)) return false;
  if (f.newOnly && !factValues(b, 'discovered_on_new_list').length) return false;
  if (f.rating !== '' && !b.facts.some((r) => r.field === 'rating' && r.source_id === f.ratingSource
    && Number((r.value as { value?: number })?.value) >= Number(f.rating))) return false;
  return true;
}

/** Distinct string values for a filter dropdown, sorted. */
export function brandOptions(brands: RadarBrand[], field: 'source' | 'country' | 'operator' | 'products' | 'licenses' | 'currencies' | 'deposit_methods'): string[] {
  const all = brands.flatMap((b) => field === 'source'
    ? b.sources.map((s) => s.source_id)
    : field === 'country'
      ? [...factValues(b, 'allowed_countries'), ...factValues(b, 'listed_market')]
      : factValues(b, field));
  return [...new Set(all.filter((v): v is string => typeof v === 'string'))].sort();
}

// ── Affiliate programs ────────────────────────────────────────────────────

export interface AffiliateFilters {
  q: string; geo: string; proof: '' | 'official_site' | 'directory_claim'; model: string;
  kind: string; source: string; vertical: string; traffic: string;
  license: string; frequency: string; negative: string; fee: string;
  caps: '' | 'yes' | 'no'; known: '' | 'yes' | 'no'; conflict: '' | 'yes' | 'no';
  rs: string; currency: '' | 'EUR' | 'USD' | 'GBP'; cpa: string;
}

export const EMPTY_AFFILIATE_FILTERS: AffiliateFilters = {
  q: '', geo: '', proof: '', model: '', kind: '', source: '', vertical: '', traffic: '',
  license: '', frequency: '', negative: '', fee: '', caps: '', known: '', conflict: '', rs: '', currency: '', cpa: '',
};

export const AFFILIATE_FIELD_FILTERS = [
  ['license', 'License'], ['frequency', 'Payout frequency'], ['negative', 'Negative balance'], ['fee', 'Admin Fee'],
] as const;

/** One currency and a number in a CPA claim, or null when it cannot be compared. */
export function cpaRate(value: string | undefined): { currency: string; maximum: number } | null {
  const s = String(value || '');
  const symbol: Record<string, string> = { '€': 'EUR', $: 'USD', '£': 'GBP' };
  const currencies = [...new Set([...s.matchAll(/\b(EUR|USD|GBP)\b|[€$£]/g)].map((m) => symbol[m[0]] || m[0]))];
  if (currencies.length !== 1 || s.includes('%')) return null;
  const numbers = [...s.matchAll(/\d{1,3}(?:,\d{3})+(?:\.\d+)?|\d+(?:\.\d+)?/g)].map((m) => Number(m[0].replace(/,/g, '')));
  return numbers.length ? { currency: currencies[0]!, maximum: Math.max(...numbers) } : null;
}

export function hostOf(url: string): string {
  try { return new URL(url).hostname; } catch { return ''; }
}

export function selectedClaims(p: AffiliateProgram, f: Pick<AffiliateFilters, 'proof'>): AffiliateClaim[] {
  return f.proof === 'official_site' ? p.claims.filter((c) => c.verification === 'official_site') : p.claims;
}

/** Every condition must hold within one source's claim, never across two. */
export function matchesAffiliate(p: AffiliateProgram, f: AffiliateFilters): boolean {
  const cs = selectedClaims(p, f);
  const hasOfficial = p.claims.some((c) => c.verification === 'official_site');
  if ((f.proof === 'directory_claim' && hasOfficial) || !cs.length) return false;
  if (f.kind && p.kind !== f.kind) return false;
  if (f.q && !(p.name + ' ' + p.claims.map((c) => c.fields?.['Brand names'] || '').join(' ')).toLowerCase().includes(f.q.toLowerCase())) return false;
  return cs.some((c) => {
    const fields = c.fields || {}, target = c.geo?.target || [], forbidden = c.geo?.forbidden || [];
    if (f.geo && (!target.includes(f.geo) || forbidden.includes(f.geo))) return false;
    if (f.model && !c.models?.includes(f.model)) return false;
    if (f.currency || f.cpa) {
      const rate = cpaRate(fields.CPA);
      if (!rate || !f.currency || rate.currency !== f.currency || (f.cpa && rate.maximum < Number(f.cpa))) return false;
    }
    if (f.source && hostOf(c.source_url) !== f.source) return false;
    if (f.vertical && !c.verticals?.includes(f.vertical)) return false;
    if (f.traffic && !c.traffic?.includes(f.traffic)) return false;
    for (const [filter, key] of AFFILIATE_FIELD_FILTERS) if (f[filter] && fields[key] !== f[filter]) return false;
    if ((f.caps === 'yes' && !fields.Caps) || (f.caps === 'no' && fields.Caps)) return false;
    if ((f.known === 'yes' && !target.length) || (f.known === 'no' && target.length)) return false;
    if ((f.conflict === 'yes' && !c.conflicts?.length) || (f.conflict === 'no' && c.conflicts?.length)) return false;
    if (f.rs && (!fields.RevShare || ![...fields.RevShare.matchAll(/(\d+(?:\.\d+)?)\s*%/g)].some((m) => Number(m[1]) >= Number(f.rs)))) return false;
    return true;
  });
}

// ── Demand ────────────────────────────────────────────────────────────────

export interface Interest { score: number; coverage: number; trendsWeight: number }

/**
 * Composite /100: Trends 50%, Apple 30%, Wordstat 10%, Bing 10%. A missing
 * source hands its weight to Trends; a zero counts as data.
 */
export function weightedInterest(trends: number | null | undefined, apple: number | null = null, wordstat: number | null = null, bing: number | null = null): Interest | null {
  if (trends == null) return null;
  const parts = [apple, wordstat, bing], weights = [0.3, 0.1, 0.1];
  let tw = 0.5, score = 0, coverage = 1;
  parts.forEach((v, i) => {
    if (v == null) tw += weights[i]!;
    else { score += v * weights[i]!; coverage++; }
  });
  return { score: Math.round(Math.min(100, Math.max(0, score + trends * tw)) * 10) / 10, coverage, trendsWeight: Math.round(tw * 100) };
}

// The countries the Motive freelancers work from, best first.
export const FREELANCER_RANKING = ['MX', 'NG', 'ID', 'ZA', 'AR', 'UA', 'AE', 'BD', 'US', 'TR', 'IN', 'UZ', 'FR', 'BR', 'VE', 'PL', 'GB', 'CA', 'DE', 'AO'];

// ── Files ─────────────────────────────────────────────────────────────────

export function downloadBlob(name: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadJson(name: string, value: unknown) {
  downloadBlob(name, new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' }));
}

/** Semicolon CSV with a BOM for Excel; formula-looking cells are defused. */
export function downloadCsv(name: string, rows: unknown[][]) {
  const cell = (v: unknown) => '"' + String(typeof v === 'string' && /^[=+\-@\t\r]/.test(v) ? "'" + v : v ?? '').replace(/"/g, '""') + '"';
  downloadBlob(name, new Blob(['﻿' + rows.map((r) => r.map(cell).join(';')).join('\r\n')], { type: 'text/csv;charset=utf-8' }));
}

export const fmtDate = (iso: string, opts?: Intl.DateTimeFormatOptions) => new Date(iso).toLocaleString('en-US', opts);

export const safeHttps = (u: string | null | undefined) => (u && /^https:\/\//.test(u) ? u : undefined);
